import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Link, Typography } from '@mui/material'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { supabase } from '../../supabaseClient'
import FairProfileDialog from './FairProfileDialog'

// The student's chosen fair, surfaced at the top of the dashboard (and parent
// portal): the fair name + link first, then the four canonical dates for the
// current competition season. A date that isn't published yet shows "TBD" so
// students still know which milestones are coming. No gating — a reminder layer.

const FALLBACK_SEASON = 2027

// The four canonical dates, in display order, with fixed descriptions (the
// meaning of each is the same across every fair).
const DATE_FIELDS: Array<{
  key: 'pre_approval_date' | 'registration_date' | 'submission_date' | 'held_date'
  label: string; preApproval?: boolean
}> = [
  { key: 'pre_approval_date', label: 'Pre-Approval Registration & SRC Review', preApproval: true },
  { key: 'registration_date', label: 'Regular Registration & SRC Review' },
  { key: 'submission_date',   label: 'Project Submission' },
  { key: 'held_date',         label: 'Fair Dates' },
]

interface FairSeason {
  pre_approval_date: string | null
  registration_date: string | null
  submission_date:   string | null
  held_date:         string | null
  dates_updated_at:  string | null
}

// `studentName` given on the parent view → "Johnny plans to compete in …".
// `canChange` opts a surface into the "Change fair" control. Off by default,
// and deliberately off on the parent portal: the fair is the student's answer
// to give. Without it there was no way to correct a mis-pick at all — the
// selection dialog only ever opened when NO fair was set, so a student who
// chose the wrong one was stuck with it, despite that dialog promising "you
// can change this anytime".
export default function ImportantDatesCard({ projectId, studentName, canChange }: {
  projectId: string; studentName?: string; canChange?: boolean
}) {
  const [fairId,     setFairId]     = useState<string | null>(null)
  const [fairName,   setFairName]   = useState<string | null>(null)
  const [fairUrl,    setFairUrl]    = useState<string | null>(null)
  const [qualifying, setQualifying] = useState<string | null>(null)
  const [season,     setSeason]     = useState<FairSeason | null>(null)
  const [loaded,     setLoaded]     = useState(false)
  const [changing,   setChanging]   = useState(false)

  const load = useCallback(async (signal?: { cancelled: boolean }) => {
    const { data: entry } = await supabase.from('project_fair_entry')
      .select('science_fair_id, qualifying_fair').eq('project_id', projectId).maybeSingle()
    if (signal?.cancelled) return
    const e = entry as { science_fair_id: string; qualifying_fair: string | null } | null
    if (!e) { setLoaded(true); return }
    setFairId(e.science_fair_id)
    setQualifying(e.qualifying_fair)

    // Resolve the active competition season, then load the fair + its dates.
    const { data: seasonSetting } = await supabase.from('app_setting')
      .select('value').eq('key', 'current_competition_season').maybeSingle()
    const curSeason = Number((seasonSetting as { value: string } | null)?.value) || FALLBACK_SEASON

    const [{ data: fair }, { data: fs }] = await Promise.all([
      supabase.from('science_fair').select('name, website').eq('id', e.science_fair_id).maybeSingle(),
      supabase.from('fair_season')
        .select('pre_approval_date, registration_date, submission_date, held_date, dates_updated_at')
        .eq('science_fair_id', e.science_fair_id).eq('season', curSeason).maybeSingle(),
    ])
    if (signal?.cancelled) return
    const f = fair as { name: string; website: string | null } | null
    setFairName(f?.name ?? null)
    setFairUrl(f?.website ?? null)
    setSeason((fs as FairSeason | null) ?? null)
    setLoaded(true)
  }, [projectId])

  useEffect(() => {
    const signal = { cancelled: false }
    load(signal)
    return () => { signal.cancelled = true }
  }, [load])

  // Only require that a fair has been chosen — dates are optional (they render
  // as TBD until published).
  if (!loaded || !fairName) return null

  const fmt = (d: string) => {
    const [y, m, day] = d.split('-').map(Number)
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }
  // Only a student who can enter directly (no qualifying school/district fair) is
  // actually "competing in" the fair. With a qualifying fair — or unknown — they
  // must be selected there first, so soften to "aiming to compete in".
  const directEntry = qualifying === 'no'
  const lead = studentName
    ? `${studentName} ${directEntry ? 'plans to compete in' : 'is aiming to compete in'}`
    : (directEntry ? "You're competing in" : "You're aiming to compete in")
  // "Recently updated" flag — dates changed within the last 21 days.
  const updatedRecently = !!season?.dates_updated_at &&
    (Date.now() - new Date(season.dates_updated_at).getTime()) < 21 * 24 * 60 * 60 * 1000

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Fair name + link — always shown when a fair is selected */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <EmojiEventsOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Your science fair
          </Typography>
          {updatedRecently && <Chip label="Dates updated" size="small" color="info" />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>{lead}</Typography>
        {fairUrl ? (
          <Link href={fairUrl} target="_blank" rel="noopener noreferrer" underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, fontSize: '1.1rem' }}>
            {fairName}<OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>
        ) : (
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{fairName}</Typography>
        )}

        {/* The four canonical dates — always shown; TBD until published */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Important dates</Typography>
        {DATE_FIELDS.map(f => {
          const raw = season?.[f.key] ?? null
          return (
            <Box key={f.key} sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 700, color: raw ? 'text.primary' : 'text.secondary' }}>
                  {raw ? fmt(raw) : 'TBD'}:
                </Box>{' '}
                {f.label}
              </Typography>
              {f.preApproval && <Chip label="Pre-approval" size="small" color="warning" />}
            </Box>
          )
        })}
        <Alert severity={qualifying === 'no' ? 'info' : 'warning'} sx={{ mt: 1 }}>
          {qualifying === 'no'
            ? 'No school or district fair — you can enter this fair directly, so these dates apply to you.'
            : 'Eligibility: this is a county fair. If your school or district holds a science fair, you must be selected there first — you are not automatically eligible. Those qualifying-fair dates are usually announced in September–October; check with your teacher.'}
        </Alert>

        {canChange && (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setChanging(true)}>
              Not the right fair? Change it
            </Button>
          </Box>
        )}
      </CardContent>

      {canChange && (
        <FairProfileDialog
          projectId={projectId}
          open={changing}
          currentFairId={fairId}
          currentQualifying={qualifying}
          onClose={() => setChanging(false)}
          onSaved={() => { setChanging(false); load() }}
        />
      )}
    </Card>
  )
}
